import React, { useState } from 'react';
import { Check, X, RotateCcw } from 'lucide-react';
import { GA, CONTRASTS, QUIZ } from './data';
import { SagittalHead } from './SagittalHead';
import { Head, Reveal } from './ui';
import { useT, useL } from '../../lib/lessonI18n';

export const GASection = () => {
  const T = useT();
  const L = useL();
  const [sel, setSel] = useState(GA[0]);
  return (
    <section className="py-12" id="ct-ga" data-testid="ga-section">
      <Head kicker={T('General American · Cosa cambia', 'General American · What changes')} title={T('Stesse consonanti, realizzazioni diverse', 'Same consonants, different realisations')} sub={T('Il sistema è quasi identico: cambiano gli allofoni. Tocca un fenomeno.', 'The system is almost identical: allophones change. Tap a phenomenon.')} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.2fr]">
        <Reveal className="self-start lg:sticky lg:top-24 rounded-3xl border border-lime-500/20 bg-slate-900/40 p-5 flex flex-col items-center">
          <div className="aspect-[17/20] w-full max-w-[260px]"><SagittalHead place={sel.place} manner={sel.manner} voiced={sel.id !== 'glottal' && sel.id !== 'wh'} testid="ga-head" /></div>
          <p className="mt-2 text-5xl font-black text-lime-300 font-mono" data-testid="ga-ipa">[{sel.ipa}]</p>
          <p className="mt-1 font-mono text-sm text-slate-300">{sel.key}</p>
        </Reveal>
        <Reveal delay={120} className="space-y-2.5">
          {GA.map((g) => (
            <button key={g.id} onClick={() => setSel(g)} data-testid={`ga-item-${g.id}`}
              className={`w-full text-left rounded-2xl border p-4 transition-all ${sel.id === g.id ? 'border-lime-400/60 bg-lime-500/[0.07]' : 'border-cyan-500/15 bg-slate-900/50 hover:border-cyan-400/40'}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-lime-500/15 font-mono text-lg font-black text-lime-300">{g.ipa}</span>
                <span className="min-w-0">
                  <span className="block font-bold text-white">{L(g.t)}</span>
                  {sel.id === g.id ? <span className="mt-1 block text-sm text-slate-300">{L(g.d)}</span> : <span className="block truncate font-mono text-xs text-slate-400">{g.key}</span>}
                </span>
              </div>
            </button>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

export const ContrastTable = () => {
  const T = useT();
  const L = useL();
  return (
    <section className="py-12" data-testid="contrast-section">
      <Head kicker={T('RP ↔ US · I contrasti chiave', 'RP ↔ US · Key contrasts')} title={T('Stessa parola, consonante diversa', 'Same word, different consonant')} />
      <Reveal className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-orange-400/25 bg-slate-900/50">
        <table className="w-full text-left text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="px-5 py-3 font-bold">{T('Parola', 'Word')}</th><th className="px-3 py-3 font-bold">RP</th><th className="px-3 py-3 font-bold">US</th></tr></thead>
          <tbody>
            {CONTRASTS.map((r) => (
              <tr key={r[0]} className="border-t border-slate-800/80" data-testid={`contrast-${r[0]}`}>
                <td className="px-5 py-2.5 font-semibold text-white">{r[0]}</td>
                <td className="ipa px-3 py-2.5 text-cyan-200">{L(r[1])}</td>
                <td className="ipa px-3 py-2.5 text-lime-200">{L(r[2])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Reveal>
    </section>
  );
};

export const Quiz = () => {
  const T = useT();
  const L = useL();
  const [answers, setAnswers] = useState({});
  const score = QUIZ.reduce((s, q, i) => s + (answers[i] === q.a ? 1 : 0), 0);
  const all = Object.keys(answers).length === QUIZ.length;
  return (
    <section className="py-12" data-testid="quiz-section">
      <Head kicker={T('Metti alla prova', 'Test yourself')} title={T('Quiz: nomina quella consonante', 'Quiz: name that consonant')} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {QUIZ.map((q, qi) => {
          const picked = answers[qi];
          return (
            <Reveal key={qi} delay={qi * 60}>
              <div data-testid={`quiz-q-${qi}`} className="h-full rounded-2xl border border-cyan-500/15 bg-slate-900/50 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500/15 text-sm font-black text-orange-300">{qi + 1}</span>
                  <p className="pt-1 text-base font-semibold text-white">{L(q.q)}</p>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {q.opts.map((o, oi) => {
                    const isPicked = picked === o; const ok = o === q.a;
                    let cls = 'border-cyan-500/25 bg-slate-950/40 text-cyan-100 hover:border-cyan-400/60';
                    if (picked) cls = ok ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-200' : isPicked ? 'border-red-400/70 bg-red-500/15 text-red-200' : 'border-slate-700/60 bg-slate-950/30 text-slate-500';
                    return (
                      <button key={o} data-testid={`quiz-${qi}-opt-${oi}`} disabled={!!picked} onClick={() => setAnswers((a) => ({ ...a, [qi]: o }))}
                        className={`relative flex items-center justify-center rounded-xl border px-1 py-2.5 font-mono text-base transition-all duration-200 ${cls}`}>
                        {o}
                        {picked && ok && <Check size={13} className="absolute right-1 top-1 text-emerald-300" />}
                        {picked && isPicked && !ok && <X size={13} className="absolute right-1 top-1 text-red-300" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-8 flex flex-col items-center gap-4 rounded-2xl border border-cyan-500/15 bg-slate-900/60 p-6 text-center">
        <div className="text-sm font-bold uppercase tracking-widest text-slate-400">{T('Punteggio', 'Score')}</div>
        <div data-testid="quiz-score" className="text-5xl font-black"><span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">{score}</span><span className="text-slate-600"> / {QUIZ.length}</span></div>
        {all && <p className="text-sm text-slate-300" data-testid="quiz-result-msg">{score === QUIZ.length ? T('Perfetto! Classifichi come un fonetista.', 'Perfect! You classify like a phonetician.') : T('Buon inizio: rivedi luogo, modo e sonorità e riprova.', 'Good start: review place, manner and voicing and try again.')}</p>}
        {Object.keys(answers).length > 0 && <button onClick={() => setAnswers({})} data-testid="quiz-reset" className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-cyan-200 transition-colors hover:border-orange-400 hover:text-orange-200"><RotateCcw size={14} /> {T('Ricomincia', 'Start again')}</button>}
      </Reveal>
    </section>
  );
};
